<template>
  <b-card
    no-body
    class="result-card"
    :class="cardBorderClass"
    style="border-width: 2px"
    @mouseenter="prepareReportBuild"
    @focusin="prepareReportBuild"
  >
    <!-- Header -->
    <b-card-header class="pb-50">
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="result-card-title">
          <span
            v-if="countryCode"
            class="flag-icon flag-icon-rounded mr-50"
            :class="`flag-icon-${countryCode}`"
          />
          <strong>{{ item.name }}</strong>
          <LottoReportRebuildButton
            v-if="canManageReportBuild && reportAvailable"
            :key="`${item.id}:${reportRoundId}`"
            ref="reportRebuild"
            class="report-rebuild-action"
            :huay-id="String(item.id)"
            :round-id="String(reportRoundId)"
            :huay-name="item.name"
          />
        </div>
        <b-badge :variant="statusVariant">
          {{ statusLabel }}
        </b-badge>
      </div>
      <small class="text-muted mt-25 d-block">
        {{ roundDateDisplay }}
      </small>
    </b-card-header>

    <hr class="my-0">

    <!-- Winning Numbers — single line, fixed height -->
    <b-card-body class="py-75">
      <div
        v-if="hasResult"
        class="d-flex align-items-baseline justify-content-center"
        style="min-height: 28px"
      >
        <template v-if="item.type === 1">
          <small class="text-muted mr-50">{{ $t('รางวัลที่1') }}</small>
          <span
            style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px"
            class="mr-1"
          >{{ d.topThree }}</span>
          <small class="text-muted mr-50">{{ $t('2ตัวล่าง') }}</small>
          <span style="font-weight: 600">{{ d.bottomTwo }}</span>
        </template>
        <template v-else-if="item.type === 5">
          <small class="text-muted mr-50">{{ $t('เลขชุด') }}</small>
          <span style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px">{{ d.fourNumber }}</span>
        </template>
        <template v-else>
          <small class="text-muted mr-50">{{ $t('3ตัวบน') }}</small>
          <span
            style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px"
            class="mr-1"
          >{{ topThreeDisplay }}</span>
          <template v-if="d.bottomTwo">
            <small class="text-muted mr-50">{{ $t('2ตัวล่าง') }}</small>
            <span style="font-weight: 600">{{ d.bottomTwo }}</span>
          </template>
        </template>
      </div>
      <div
        v-else
        class="text-muted text-center"
        style="min-height: 28px; line-height: 28px"
      >
        <small>{{ $t('ปิดรอบ:') }}</small> {{ item.roundEndDate || '-' }}
      </div>
    </b-card-body>

    <hr class="my-0">

    <!-- Financial Summary -->
    <b-card-body class="pt-1">
      <template v-if="d.summary">
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดแทง') }}</span>
          <span>฿{{ formatNumber(d.summary.sumRollingAmount) }}</span>
        </div>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดจ่าย') }}</span>
          <span>฿{{ formatNumber(d.summary.sumWinAmount) }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ $t('กำไร/ขาดทุน') }}</span>
          <span :class="profitColorClass">
            {{ profitPrefix }}฿{{ formatNumber(Math.abs(profit)) }}
          </span>
        </div>
      </template>
      <template v-else>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดแทง') }}</span>
          <span class="text-muted">-</span>
        </div>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดจ่าย') }}</span>
          <span class="text-muted">-</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ $t('กำไร/ขาดทุน') }}</span>
          <span class="text-muted">-</span>
        </div>
      </template>
    </b-card-body>

    <template v-if="canViewReport">
      <hr class="my-0">

      <b-card-body class="report-actions-body">
        <div class="report-actions-grid">
          <b-button
            v-for="action in reportActions"
            :key="action.view"
            variant="none"
            size="sm"
            :class="['report-action-button', action.className]"
            :disabled="!reportAvailable"
            :title="reportActionTitle(action)"
            @click="$emit('open-report', action.view)"
          >
            <feather-icon
              :icon="action.icon"
              size="15"
              class="mr-50"
            />
            <span>{{ $t(action.label) }}</span>
          </b-button>
        </div>
      </b-card-body>
    </template>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BButton,
} from 'bootstrap-vue'
import { NumberFormatter } from '@core/utils/filter'
import { getCountryCode } from '@core/utils/countryFlag'
import LottoReportRebuildButton from './LottoReportRebuildButton.vue'

export default {
  components: {
    LottoReportRebuildButton,
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BButton,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    useLastResult: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // ข้อความไทยดิบเช่นเดียวกับ statusText ข้างล่าง — แปลตอนใช้ ไม่ใช่ตอนสร้าง
      // เพราะ data() ทำงานครั้งเดียว ค่าที่แปลไว้จะค้างเป็นภาษาของตอนนั้นตลอดไป
      reportActions: [
        {
          view: 'type',
          label: 'ยอดตามประเภท',
          icon: 'BarChart2Icon',
          className: 'report-action-type',
        },
        {
          view: 'number',
          label: 'ยอดตามหมายเลข',
          icon: 'HashIcon',
          className: 'report-action-number',
        },
        {
          view: 'member',
          label: 'ยอดตามสมาชิก',
          icon: 'UsersIcon',
          className: 'report-action-member',
        },
        {
          view: 'items',
          label: 'ยอดตามรายการ',
          icon: 'ListIcon',
          className: 'report-action-items',
        },
      ],
      statusText: [
        'ยังไม่ถึงเวลาแทง',
        'เปิดรับแทง',
        'พักชั่วคราว',
        'ปิดรับแทง',
        'กำลังคำนวณ',
        'คำนวณเรียบร้อย',
        'รอจ่าย',
        'กำลังจ่าย',
        'คืนโพย',
        'จ่ายเสร็จ',
      ],
      statusColorMap: {
        0: 'secondary',
        1: 'success',
        2: 'danger',
        3: 'warning',
        4: 'secondary',
        5: 'dark',
        6: 'warning',
        7: 'info',
        8: 'secondary',
        9: 'light-secondary',
      },
    }
  },
  computed: {
    // เลือกข้อมูลตาม mode: lastResult หรือ current
    d() {
      if (this.useLastResult && this.item.lastResult) return this.item.lastResult
      return this.item
    },
    reportRoundId() {
      return this.d.roundId || ''
    },
    displayedStatus() {
      if (!this.useLastResult || !this.reportRoundId || this.reportRoundId === this.item.roundId) {
        return this.item.status
      }
      // Older API responses omit lastResult.status; their history query only selects status 9.
      return this.d.status === undefined || this.d.status === null ? 9 : this.d.status
    },
    reportAvailable() {
      return Boolean(this.reportRoundId) && Number(this.displayedStatus) === 9
    },
    canViewReport() {
      return this.$can('read', 'report')
        || this.$can('update', 'report')
        || this.$can('manage', 'report')
    },
    canManageReportBuild() {
      return this.$store.state.app.isMaster(this.$store) && this.$can('manage', 'all')
    },
    hasResult() {
      if (this.item.type === 5) return !!this.d.fourNumber
      return !!this.topThreeDisplay || !!this.d.bottomTwo
    },
    countryCode() {
      return getCountryCode(this.item.name)
    },
    statusLabel() {
      return this.$t(this.statusText[this.displayedStatus] || 'ไม่ทราบสถานะ')
    },
    statusVariant() {
      return this.statusColorMap[this.displayedStatus] || 'secondary'
    },
    roundDateDisplay() {
      if (!this.item.roundDate) return ''
      if (this.useLastResult && this.d.roundDate && this.d.roundDate !== this.item.roundDate) {
        return this.$t('ผลรอบ: {date}', { date: this.d.roundDate.split(' ')[0] })
      }
      return this.$t('รอบ: {date}', { date: this.item.roundDate.split(' ')[0] })
    },
    topThreeDisplay() {
      if (!this.d.topThree) return ''
      if (this.d.topThree.length >= 3) {
        return this.d.topThree.substring(this.d.topThree.length - 3)
      }
      return this.d.topThree
    },
    profit() {
      if (!this.d.summary) return 0
      return this.d.summary.sumRollingAmount - this.d.summary.sumWinAmount
    },
    profitPrefix() {
      if (this.profit > 0) return '+'
      if (this.profit < 0) return '-'
      return ''
    },
    profitColorClass() {
      if (this.profit > 0) return 'text-success font-weight-bold'
      if (this.profit < 0) return 'text-danger font-weight-bold'
      return ''
    },
    cardBorderClass() {
      if (!this.d.summary) return ''
      if (this.profit > 0) return 'border-success'
      if (this.profit < 0) return 'border-danger'
      return ''
    },
  },
  methods: {
    prepareReportBuild() {
      if (this.$refs.reportRebuild) this.$refs.reportRebuild.prepare()
    },
    reportActionTitle(action) {
      if (this.reportAvailable) return this.$t(action.label)
      if (this.reportRoundId) return this.$t('ดูรายงานได้เมื่อรอบจ่ายเสร็จแล้ว')
      return this.$t('ยังไม่มีข้อมูลรอบสำหรับดูรายงาน')
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '0.00'
      return NumberFormatter(Number(val).toFixed(2))
    },
  },
}
</script>

<style scoped>
.result-card-title {
  align-items: center;
  display: flex;
  min-width: 0;
  padding-right: 0.5rem;
}
.result-card-title > .flag-icon {
  flex-shrink: 0;
}
.result-card-title > strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-rebuild-action {
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease;
}
.result-card:hover .report-rebuild-action,
.result-card:focus-within .report-rebuild-action,
.report-rebuild-action.is-active {
  opacity: 1;
  pointer-events: auto;
}
@media (hover: none), (pointer: coarse) {
  .report-rebuild-action {
    opacity: 1;
    pointer-events: auto;
  }
}
.report-actions-body {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}
.report-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}
.report-action-button {
  align-items: center;
  border: 0;
  border-radius: 0.55rem;
  box-shadow: 0 4px 10px rgba(34, 31, 68, 0.13);
  color: #fff;
  display: inline-flex;
  font-weight: 600;
  justify-content: center;
  min-height: 40px;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
  transition: box-shadow 160ms ease, filter 160ms ease, transform 160ms ease;
  white-space: nowrap;
}
.report-action-button:hover:not(:disabled) {
  box-shadow: 0 7px 14px rgba(34, 31, 68, 0.2);
  color: #fff;
  filter: brightness(1.06);
  transform: translateY(-1px);
}
.report-action-button:focus,
.report-action-button:active {
  box-shadow: 0 0 0 0.2rem rgba(114, 92, 255, 0.22);
  color: #fff;
}
.report-action-button:disabled {
  box-shadow: none;
  color: #fff;
  cursor: not-allowed;
  filter: saturate(0.55);
  opacity: 0.48;
}
.report-action-type {
  background: #5b16d9;
}
.report-action-number {
  background: #35bd7b;
}
.report-action-member {
  background: #f53669;
}
.report-action-items {
  background: #e9a400;
}
@media (max-width: 360px) {
  .report-actions-grid {
    grid-template-columns: 1fr;
  }
}
</style>
